import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { IconCheck, IconChevronRight, IconClock, IconDots, IconFileText, IconHourglass, IconLock, IconAlertTriangle, IconBell, IconPlus, IconSearch, IconTrash } from '@tabler/icons-react';
import { useT } from '@hana/i18n';
import { HeaderTool, Page, PageHeader } from '@hana/shell';
import { Button, Skeleton, StateView, useToast } from '@hana/ui';
import { financeScreenState, useFinanceAccess } from '../finance/access';
import { norm } from '../finance/view';
import { PAD_KIND, PAD_KINDS, PAD_PERIODS, PAD_ROWS, PAD_SALES, PAD_STATUS, padBadge, padF1, padFilter, type PadRow, type PadStatus, type PadTone } from './invm';
import type { InvDoc } from './invd';
import './invpad.i18n';
import s from './invm.module.css';
import a from './arm.module.css';

// Mint-Invoice-iPad (1180): tab trạng thái · tìm · lọc Kỳ / Sale / Loại · danh sách một bảng 21 chứng từ. TIỀN → sau quyền `money`. Chỉ đọc; nút ghi báo toast.
const DOC: Readonly<Record<PadStatus, InvDoc>> = { draft: 'nhap', issued: 'phathanh', sent: 'dagui', paid: 'dathu', over: 'quahan' };
const DOT: Readonly<Record<PadStatus, string>> = { draft: 'var(--lb)', issued: 'var(--d-violet, #8380E8)', sent: 'var(--d-blue)', paid: 'var(--ok)', over: 'var(--err)' }; // token-ok: màu chấm trạng thái theo board
const TONE: Readonly<Record<PadTone, string>> = { ok: s.tOk ?? '', warn: s.tWarn ?? '', err: s.tErr ?? '', pri: s.tCtl ?? '', ctl: s.tCtl ?? '' };
const ICON = (r: PadRow, tone: PadTone) => (r.st === 'paid' ? <IconCheck size={14} aria-hidden="true" /> : r.st === 'draft' && r.sub === 'dcf' ? <IconBell size={14} aria-hidden="true" /> : r.st === 'draft' && r.sub === 'fix' ? <IconAlertTriangle size={14} aria-hidden="true" /> : r.st === 'draft' && r.sub === 'ok' ? <IconFileText size={14} aria-hidden="true" /> : r.st === 'draft' ? <IconHourglass size={14} aria-hidden="true" /> : <IconClock size={14} aria-hidden="true" data-tone={tone} />);

type Menu = 'per' | 'sale' | 'kind' | null;

export function InvoicePad() {
  const t = useT();
  const toast = useToast();
  const access = useFinanceAccess();
  const state = financeScreenState(access);
  const [st, setSt] = useState<'all' | PadStatus>('all');
  const [q, setQ] = useState('');
  const [per, setPer] = useState('3m');
  const [sale, setSale] = useState('all');
  const [kind, setKind] = useState('all');
  const [menu, setMenu] = useState<Menu>(null);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!menu) return;
    const off = (e: MouseEvent) => { if (!ref.current?.contains(e.target as Node)) setMenu(null); };
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') setMenu(null); };
    document.addEventListener('mousedown', off); document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('mousedown', off); document.removeEventListener('keydown', esc); };
  }, [menu]);
  const m = (x: string) => (access.money ? x : '•••••');
  const f = { st, per, sale, kind, q };
  const base = useMemo(() => padFilter(PAD_ROWS, f, norm, 'st'), [per, sale, kind, q]); // eslint-disable-line react-hooks/exhaustive-deps
  const rows = useMemo(() => base.filter((x) => st === 'all' || x.st === st), [base, st]);
  const sum = rows.reduce((acc, x) => acc + x.amt, 0);
  const over = rows.filter((x) => x.st === 'over').reduce((acc, x) => acc + x.amt, 0);
  const hasFlt = per !== '3m' || sale !== 'all' || kind !== 'all' || q.trim() !== '';
  const clear = () => { setPer('3m'); setSale('all'); setKind('all'); setQ(''); };
  const noWrite = () => toast({ msg: t('ip.noWrite') });
  const ready = state === 'ready';
  const tabs: readonly (readonly ['all' | PadStatus, string, number])[] = [['all', t('ip.all'), base.length], ...PAD_STATUS.map(([k, l]) => [k, l, base.filter((x) => x.st === k).length] as const)];
  const mixTot = rows.reduce((acc, x) => acc + Math.abs(x.amt), 0) || 1;
  const mix = PAD_STATUS.map(([k, l]) => ({ k, l, v: rows.filter((x) => x.st === k).reduce((acc, x) => acc + Math.abs(x.amt), 0) })).filter((x) => x.v > 0);

  const filters: readonly (readonly [Exclude<Menu, null>, string, string, readonly (readonly [string, string])[], (v: string) => void, string])[] = [
    ['per', t('ip.fPer'), per, PAD_PERIODS, setPer, '3m'],
    ['sale', t('ip.fSale'), sale, [['all', t('ip.allSale')], ...PAD_SALES.map((x) => [x, x] as const)], setSale, 'all'],
    ['kind', t('ip.fKind'), kind, [['all', t('ip.allKind')], ...PAD_KINDS], setKind, 'all'],
  ];

  return (
    <Page kind="table">
      <PageHeader
        title={t('ip.title')}
        findHint={false}
        tabs={
          <div className={a.tabs} role="tablist" aria-label={t('ip.tabsAria')}>
            {tabs.map(([k, l, n]) => <button key={k} type="button" role="tab" aria-selected={st === k} onClick={() => setSt(k)}>{l}{ready && <span> {n}</span>}</button>)}
          </div>
        }
        actions={
          <>
            <HeaderTool label={t('ip.exportMisa')} icon={IconDots} onClick={() => toast({ msg: t('ip.exportOff') })} />
            <Button variant="primary" icon={<IconPlus size={16} aria-hidden="true" />} onClick={noWrite}>{t('ip.mkDebit')}</Button>
          </>
        }
      />
      <div className={a.page}>
        <StateView state={state} skeleton={<Skeleton rows={9} />}>
          <>
            <div className={s.filters} ref={ref}>
              <label className={s.search}>
                <IconSearch size={17} aria-hidden="true" />
                <input aria-label={t('ip.findAria')} placeholder={t('ip.findPh')} value={q} onChange={(e) => setQ(e.target.value)} />
              </label>
              {filters.map(([k, l, v, opts, set, def]) => (
                <div key={k} className={s.fwrap}>
                  <button type="button" className={s.fbtn} aria-haspopup="listbox" aria-expanded={menu === k} aria-pressed={v !== def} onClick={() => setMenu(menu === k ? null : k)}>{l} ▾</button>
                  {menu === k && (
                    <div role="listbox" aria-label={l} className={s.fmenu}>
                      {opts.map(([ov, ol]) => <button key={ov} type="button" role="option" aria-selected={v === ov} onClick={() => { set(ov); setMenu(null); }}>{ol}</button>)}
                    </div>
                  )}
                </div>
              ))}
              {hasFlt && <button type="button" className={s.clear} onClick={clear}><IconTrash size={16} aria-hidden="true" />{t('ip.clear')}</button>}
              <div className={s.sum}>
                <span>{t('ip.docs', { n: rows.length })}</span><b>{m(`${padF1(sum)} tr`)}</b>
                {over > 0 && <span className={s.od} title={t('ip.overTip')}><i aria-hidden="true" />{m(`${padF1(over)} tr`)}</span>}
              </div>
            </div>
            {rows.length === 0 ? (
              <StateView state="empty" title={t('ip.emptyT')} desc={t('ip.emptyD')} />
            ) : (
              <section className={a.card} aria-label={t('ip.listAria')}>
                <div className={`${s.grid} ${s.head}`} aria-hidden="true">
                  <span /><span>{t('ip.cInv')}</span><span>{t('ip.cCus')}</span><span>{t('ip.cJob')}</span><span>{t('ip.cDue')}</span><span>{t('ip.cAmt')}</span><span />
                </div>
                {rows.map((r) => {
                  const b = padBadge(r);
                  const k = PAD_KIND[r.kind];
                  const [stK] = PAD_STATUS.find(([x]) => x === r.st)!.slice(1);
                  return (
                    <Link key={r.id} to="/ezy/invoice/$doc" params={{ doc: DOC[r.st] }} className={`${s.grid} ${s.row}`} aria-label={t('ip.rowAria', { id: r.id, cus: r.cus, st: stK ?? '', amt: padF1(r.amt) })} title={stK}>
                      <span role="img" aria-label={stK} className={s.dot} style={{ background: DOT[r.st] }} />
                      <span className={s.id}>
                        <b>{r.id}</b>
                        {r.lock && <IconLock size={13} role="img" aria-label={t('ip.locked')} />}
                        {r.kind !== 'dn' && r.kind !== 'hd' && <span className={s.kind} title={k[1]}>{k[0]}</span>}
                      </span>
                      <span className={s.ell}>{r.cus}</span>
                      <span className={`${s.ell} ${s.job}`}>{r.job}</span>
                      <span className={`${s.bd} ${TONE[b.tone]}`} title={b.tip}>{ICON(r, b.tone)}{b.k}</span>
                      <b className={`${s.amt} ${r.amt < 0 ? s.neg : ''}`}>{m(padF1(r.amt))}</b>
                      <IconChevronRight size={16} aria-hidden="true" className={s.chev} />
                    </Link>
                  );
                })}
                <div className={`${s.grid} ${s.tot}`}>
                  <span /><span>{t('ip.total', { n: rows.length })}</span>
                  <span className={s.mix} role="img" aria-label={t('ip.mixAria', { list: mix.map((x) => `${x.l} ${padF1(x.v)} tr`).join(', ') })}>
                    {mix.map((x) => <i key={x.k} title={`${x.l} · ${padF1(x.v)} tr`} style={{ width: `${Math.max(2, Math.round((x.v / mixTot) * 360))}px`, background: DOT[x.k] }} />)}
                  </span>
                  <span /><span /><span style={{ textAlign: 'right' }}>{m(padF1(sum))}</span><span />
                </div>
              </section>
            )}
          </>
        </StateView>
      </div>
    </Page>
  );
}
